import { memo } from "react"

import { useTranslation } from "react-i18next"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { SearchData } from "../types"

import { EmptyState } from "@/components/ui/empty-state"

import { getEmptyStates } from "./admin-dashboard-empty.utils"
import { LoadingSkeleton } from "./admin-dashboard-loading"

const CHART_HEIGHT = {
  LARGE: 320,
} as const

export const SearchStatsChart = memo(function SearchStatsChart({
  data,
  isLoading,
}: {
  data: SearchData[] | null | undefined
  isLoading?: boolean
}) {
  const { t } = useTranslation()
  const EMPTY_STATES = getEmptyStates(t)

  if (isLoading) {
    return <LoadingSkeleton height={CHART_HEIGHT.LARGE} variant="chart" />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center">
        <EmptyState
          description={EMPTY_STATES.searches.description}
          icon={EMPTY_STATES.searches.icon}
          title={EMPTY_STATES.searches.title}
        />
      </div>
    )
  }

  // Calculate interval for x-axis labels to avoid overcrowding
  const xAxisInterval = data.length > 15 ? Math.floor(data.length / 6) : 0

  return (
    <ResponsiveContainer
      aria-label="AI search statistics bar chart showing monthly search volume"
      height={CHART_HEIGHT.LARGE}
      width="100%"
    >
      <BarChart data={data}>
        <CartesianGrid className="stroke-muted" strokeDasharray="3 3" />
        <XAxis
          angle={-45}
          className="text-muted-foreground"
          dataKey="label"
          height={70}
          interval={xAxisInterval}
          minTickGap={20}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />
        <YAxis
          allowDecimals={false}
          className="text-muted-foreground"
          domain={[0, "auto"]}
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => {
            if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
            if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
            return value.toString()
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "0.5rem",
            color: "hsl(var(--popover-foreground))",
          }}
          itemStyle={{
            color: "hsl(var(--popover-foreground))",
          }}
          labelStyle={{
            color: "hsl(var(--popover-foreground))",
          }}
        />
        <Legend />
        <Bar dataKey="searches" fill="hsl(var(--chart-1))" radius={[10, 10, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
})
